(function () {
  const Core = window.RB.Core;
  const Play = window.RB.Play;

  function combinations(arr, k) {
    if (k === 0) return [[]];
    if (k > arr.length) return [];
    const out = [];
    const idx = [];
    for (let i = 0; i < k; i++) idx.push(i);
    while (true) {
      out.push(idx.map(function (i) { return arr[i]; }));
      let pos = k - 1;
      while (pos >= 0 && idx[pos] === arr.length - k + pos) pos--;
      if (pos < 0) break;
      idx[pos]++;
      for (let i = pos + 1; i < k; i++) idx[i] = idx[i - 1] + 1;
    }
    return out;
  }

  function permutations(arr, cap) {
    if (arr.length <= 1) return [arr.slice()];
    const out = [];
    const walk = function (prefix, rest) {
      if (out.length >= cap) return;
      if (!rest.length) { out.push(prefix.slice()); return; }
      for (let i = 0; i < rest.length; i++) {
        prefix.push(rest[i]);
        walk(prefix, rest.slice(0, i).concat(rest.slice(i + 1)));
        prefix.pop();
      }
    };
    walk([], arr);
    return out;
  }

  function playCands(state, p) {
    const pl = state.players[p];
    const cands = [];
    for (const iid of pl.hand) cands.push([iid, "hand"]);
    for (const iid of pl.champion) cands.push([iid, "champion"]);
    for (const bfIid of state.battlefields) {
      for (const fd of RB.Flow.hiddenAt(state, bfIid)) cands.push([fd, "facedown"]);
    }
    for (const iid of pl.trash) cands.push([iid, "trash"]);
    return cands;
  }

  function abilitySources(state, p) {
    return Core.effectSources(state, { battlefields: false }).filter(function (iid) {
      return state.objs[iid].controller === p;
    });
  }

  function lethalOf(state, iid) { return Core.lethalLeft(state, iid); }

  function cardPlays(state, p, timing) {
    const out = [];
    for (const pair of playCands(state, p)) {
      if (!Play.playBlocked(state, p, pair[0], timing)) out.push({ type: "PLAY_CARD", player: p, iid: pair[0] });
    }
    return out;
  }

  function abilityActivations(state, p, timing) {
    const out = [];
    for (const iid of abilitySources(state, p)) {
      const abList = RB.Scripts.abilitiesOf(state, iid);
      for (let ai = 0; ai < abList.length; ai++) {
        if (!Play.abilityBlocked(state, p, iid, ai, timing)) out.push({ type: "ACTIVATE", player: p, iid: iid, ability: ai });
      }
    }
    return out;
  }

  function standardMoves(state, p) {
    const out = [];
    const units = Core.boardObjs(state, function (o, iid) { return o.controller === p && Core.isType(state, iid, "unit"); });
    const groupable = {};
    for (const iid of units) {
      for (const dest of Play.moveDests(state, p, [iid])) {
        out.push({ type: "STANDARD_MOVE", player: p, units: [iid], dest: dest });
        const key = dest.t === "bf" ? dest.iid : "base";
        groupable[key] = (groupable[key] || 0) + 1;
        if (groupable[key] === 2) out.push({ type: "MOVE_GROUP", player: p, dest: dest });
      }
    }
    return out;
  }

  function hides(state, p) {
    const out = [];
    const costs = Play.hideCosts(state, p);
    for (const bfIid of state.battlefields) {
      for (const iid of state.players[p].hand) {
        for (let ci = 0; ci < costs.length; ci++) {
          if (!Play.hideBlocked(state, p, iid, bfIid, ci)) out.push({ type: "HIDE", player: p, iid: iid, bf: bfIid, costIdx: ci });
        }
      }
    }
    return out;
  }

  function assignDists(state, pd) {
    const noTank = !!(state.showdown && Core.ignoresTankAt(state, pd.player, state.showdown.bf));
    const tank = noTank ? [] : pd.targets.filter(function (i) { return Core.hasKw(state, i, "tank"); });
    const back = pd.targets.filter(function (i) { return Core.hasKw(state, i, "backline"); });
    const mid = pd.targets.filter(function (i) { return tank.indexOf(i) < 0 && back.indexOf(i) < 0; });
    const seen = {};
    const dists = [];
    const addDist = function (dist) {
      const key = JSON.stringify(pd.targets.map(function (t) { return dist[t] || 0; }));
      if (seen[key]) return;
      seen[key] = true;
      dists.push(dist);
    };
    const tankPerms = permutations(tank, 24);
    const midPerms = permutations(mid, 24);
    const backPerms = permutations(back, 24);
    for (const tp of tankPerms) {
      for (const mp of midPerms) {
        for (const bp of backPerms) {
          if (dists.length >= 400) break;
          const order = tp.concat(mp, bp);
          const dist = {};
          let left = pd.total;
          let allLethal = true;
          for (const iid of order) {
            if (left <= 0) { allLethal = false; break; }
            const need = lethalOf(state, iid);
            const give = Math.min(need, left);
            dist[iid] = give;
            if (give < need) allLethal = false;
            left -= give;
          }
          if (left > 0 && allLethal) {
            addDist(Object.assign({}, dist));
            for (const iid of order) {
              const over = Object.assign({}, dist);
              over[iid] = (over[iid] || 0) + left;
              addDist(over);
            }
          } else {
            addDist(dist);
          }
        }
      }
    }
    return dists.map(function (d) {
      return { type: "ASSIGN", player: pd.player, dist: d };
    });
  }

  function assignLimits(state, pd, dist) {
    const d = dist || {};
    const at = function (iid) { return d[iid] || 0; };
    const ignoreTank = !!(state.showdown && Core.ignoresTankAt(state, pd.player, state.showdown.bf));
    const rank = function (iid) {
      if (!ignoreTank && Core.hasKw(state, iid, "tank")) return 0;
      if (Core.hasKw(state, iid, "backline")) return 2;
      return 1;
    };
    const info = {};
    let placed = 0;
    let capacity = 0;
    for (const iid of pd.targets) {
      info[iid] = { lethal: lethalOf(state, iid), rank: rank(iid), marks: Core.totalDamage(state.objs[iid]) };
      placed += at(iid);
      capacity += info[iid].lethal;
    }
    const remaining = Math.max(0, pd.total - placed);
    const groupFull = function (r) {
      return pd.targets.every(function (iid) {
        return info[iid].rank !== r || at(iid) >= info[iid].lethal;
      });
    };
    const tankFull = groupFull(0);
    const midFull = groupFull(1);
    const out = {};
    for (const iid of pd.targets) {
      const nfo = info[iid];
      let gate = null;
      if (nfo.rank > 0 && !tankFull) gate = "tank";
      else if (nfo.rank === 2 && !midFull) gate = "backline";
      const othersLethal = pd.targets.every(function (o) {
        return o === iid || at(o) >= info[o].lethal;
      });
      const ceiling = othersLethal ? at(iid) + remaining : Math.min(nfo.lethal, at(iid) + remaining);
      out[iid] = {
        value: at(iid),
        lethal: nfo.lethal,
        marks: nfo.marks,
        rank: nfo.rank,
        gate: gate,
        max: gate ? 0 : ceiling,
        min: 0,
      };
    }
    out.$ = {
      placed: placed,
      remaining: remaining,
      total: pd.total,
      required: Math.min(pd.total, capacity),
    };
    return out;
  }

  function assignProblem(state, pd, dist) {
    const lim = assignLimits(state, pd, dist);
    const at = function (iid) { return (dist || {})[iid] || 0; };
    if (lim.$.placed < lim.$.required) return "Place all " + lim.$.required + " damage.";
    if (lim.$.placed > pd.total) return "That is more damage than you have.";
    const allLethal = pd.targets.every(function (iid) { return at(iid) >= lim[iid].lethal; });
    const partials = pd.targets.filter(function (iid) { return at(iid) > 0 && at(iid) < lim[iid].lethal; });
    for (const iid of pd.targets) {
      if (at(iid) > lim[iid].lethal && !allLethal) {
        return Core.name(state, iid) + " cannot take more than lethal while another unit is still standing.";
      }
    }
    if (partials.length > 1) return "Only one unit may be left wounded.";
    if (partials.length === 1 && lim.$.remaining > 0) {
      return Core.name(state, partials[0]) + " can only be left wounded once every point is placed.";
    }
    for (const iid of pd.targets) {
      if (at(iid) > 0 && lim[iid].gate === "tank") return "Tank units take lethal damage first.";
      if (at(iid) > 0 && lim[iid].gate === "backline") return "Backline units take damage last.";
    }
    return null;
  }

  function blockedPlays(state, p, timing, out) {
    for (const pair of playCands(state, p)) {
      const o = state.objs[pair[0]];
      if (!o || o.owner !== p) continue;
      const why = Play.playBlocked(state, p, pair[0], timing, true);
      if (why) out.push({ what: "play", iid: pair[0], zone: pair[1], name: Core.name(state, pair[0]), reason: why });
    }
  }

  function blockedAbilities(state, p, timing, out) {
    for (const iid of abilitySources(state, p)) {
      const abList = RB.Scripts.abilitiesOf(state, iid);
      for (let ai = 0; ai < abList.length; ai++) {
        const why = Play.abilityBlocked(state, p, iid, ai, timing, true);
        if (why) {
          out.push({
            what: "ability", iid: iid, ability: ai, name: Core.name(state, iid),
            label: abList[ai].label || null, reason: why,
          });
        }
      }
    }
  }

  function blockedMoves(state, p, out) {
    const units = Core.boardObjs(state, function (o, iid) {
      return o.controller === p && Core.isType(state, iid, "unit");
    });
    for (const iid of units) {
      if (state.objs[iid].attachedTo) continue;
      let reason = null;
      let free = false;
      for (const bfIid of state.battlefields) {
        const why = Play.moveBlocked(state, p, [iid], { t: "bf", iid: bfIid }, true);
        if (!why) { free = true; break; }
        if (!reason) reason = why;
      }
      if (free) continue;
      if (!Play.moveBlocked(state, p, [iid], { t: "base", p: p }, true)) continue;
      if (reason) out.push({ what: "move", iid: iid, name: Core.name(state, iid), reason: reason });
    }
  }

  function blockedHides(state, p, out) {
    const costs = Play.hideCosts(state, p);
    for (const iid of state.players[p].hand) {
      if (!Core.hasKw(state, iid, "hidden")) continue;
      let reason = null;
      let free = false;
      for (const bfIid of state.battlefields) {
        for (let ci = 0; ci < costs.length; ci++) {
          const why = Play.hideBlocked(state, p, iid, bfIid, ci, true);
          if (!why) { free = true; break; }
          if (!reason) reason = why;
        }
        if (free) break;
      }
      if (!free && reason) out.push({ what: "hide", iid: iid, name: Core.name(state, iid), reason: reason });
    }
  }

  const Enum = {
    assignLimits: assignLimits,
    assignProblem: assignProblem,

    blocked(state) {
      const pd = state.pending;
      if (!pd || state.over) return [];
      const p = pd.player;
      const out = [];
      if (pd.kind !== "act" && pd.kind !== "focus" && pd.kind !== "respond") return out;
      blockedPlays(state, p, pd.kind, out);
      blockedAbilities(state, p, pd.kind, out);
      if (pd.kind === "act") {
        blockedMoves(state, p, out);
        blockedHides(state, p, out);
      }
      return out;
    },

    actions(state) {
      const pd = state.pending;
      if (!pd || state.over) return [];
      const p = pd.player;
      const out = [];

      if (pd.kind === "mulligan") {
        const hand = state.players[p].hand;
        for (let k = 0; k <= Math.min(2, hand.length); k++) {
          for (const combo of combinations(hand, k)) {
            out.push({ type: "MULLIGAN", player: p, cards: combo });
          }
        }
        return out;
      }

      if (pd.kind === "act") {
        for (const a of cardPlays(state, p, "act")) out.push(a);
        for (const a of standardMoves(state, p)) out.push(a);
        for (const a of hides(state, p)) out.push(a);
        for (const a of abilityActivations(state, p, "act")) out.push(a);
        out.push({ type: "END_TURN", player: p });
        return out;
      }

      if (pd.kind === "focus" || pd.kind === "respond") {
        for (const a of cardPlays(state, p, pd.kind)) out.push(a);
        for (const a of abilityActivations(state, p, pd.kind)) out.push(a);
        out.push({ type: "PASS", player: p });
        return out;
      }

      if (pd.kind === "choose") {
        if (pd.options) {
          for (let i = 0; i < pd.options.length; i++) {
            out.push({ type: "CHOOSE", player: p, optionIdx: i });
          }
          if (pd.tag === "name" && !pd.options.length) {
            const universe = RB.FX.nameUniverse(pd.nameKind || "tag", pd.nameType || null);
            if (universe.length) out.push({ type: "CHOOSE", player: p, name: universe[0] });
          }
          return out;
        }
        const lo = pd.min !== undefined ? pd.min : 0;
        const hi = Math.min(pd.max !== undefined ? pd.max : 1, pd.cands.length);
        for (let k = lo; k <= hi; k++) {
          if (k === 0 && lo > 0) continue;
          for (const combo of combinations(pd.cands, k)) {
            if (!Play.cohereOk(state, pd, combo)) continue;
            out.push({ type: "CHOOSE", player: p, picks: combo });
            if (out.length >= 500) return out;
          }
        }
        if (pd.cancellable) out.push({ type: "CANCEL", player: p });
        return out;
      }

      if (pd.kind === "pay") {
        if (Play.poolCovers(state.players[p].pool, pd.cost, pd.payCtx)) {
          out.push({ type: "PAY", player: p, activations: [] });
        }
        if (Play.autoPayPlan(state, p, pd.cost, pd.payCtx)) {
          out.push({ type: "PAY", player: p, auto: true });
        }
        if (pd.cancellable) out.push({ type: "CANCEL", player: p });
        return out;
      }

      if (pd.kind === "moveGroup") {
        for (const iid of pd.cands) out.push({ type: "CHOOSE", player: p, picks: [iid] });
        if (pd.canGo) out.push({ type: "CONTINUE", player: p });
        return out;
      }

      if (pd.kind === "assign") return assignDists(state, pd);

      if (pd.kind === "manual") return [{ type: "CONTINUE", player: p }];

      return out;
    },
  };

  window.RB.Enum = Enum;
})();
